import extractErrorMessage from '../../../shared/utils/extractErrorMessage';
import { authClient } from '../../../shared/api/client';

const isOkStatus = (status) => status >= 200 && status < 300;

// Lista svih razgovora za nalog apoteke (vec sortirano po najnovijoj aktivnosti).
// `search` je jedan termin za pretragu po imenu/prezimenu pacijenta; pretraga i
// paginacija idu na backend (indeksirano), pa je skalabilno na velikom skupu.
export const fetchConversations = async ({
  page = 1,
  limit = 20,
  search = '',
} = {}) => {
  try {
    const trimmedSearch = typeof search === 'string' ? search.trim() : '';
    let query = `?page=${page}&limit=${limit}`;
    if (trimmedSearch) {
      query += `&search=${encodeURIComponent(trimmedSearch)}`;
    }

    const response = await authClient.get(`/conversations${query}`);
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri dobavljanju razgovora.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

// Zapocinjanje razgovora sa pacijentom sa dashboarda (get-or-create). Backend
// vraca postojeci razgovor za pacijenta ili pravi novi, i vraca ceo Conversation
// objekat (sa `id`). Idempotentno po pacijentu.
export const startConversation = async (patientId) => {
  try {
    const response = await authClient.post('/conversations', { patientId });
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri pokretanju razgovora.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

// Istorija poruka jednog razgovora (najnovije prvo). Za starije poruke prosledi
// createdAt najstarije ucitane poruke kao `before`.
export const fetchMessages = async (conversationId, { before, limit = 30 } = {}) => {
  try {
    let query = `?limit=${limit}`;
    if (before) {
      query += `&before=${encodeURIComponent(before)}`;
    }

    const response = await authClient.get(
      `/conversations/${conversationId}/messages${query}`
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri dobavljanju poruka.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

// Popunjavanje "rupe" posle reconnect-a (najstarije prvo) - poruke novije od
// createdAt poslednje poznate poruke.
export const syncMessages = async (conversationId, { after, limit = 100 } = {}) => {
  try {
    let query = `?limit=${limit}`;
    if (after) {
      query += `&after=${encodeURIComponent(after)}`;
    }

    const response = await authClient.get(
      `/conversations/${conversationId}/messages/sync${query}`
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri sinhronizaciji poruka.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

// Brise "nepročitano" oznaku za apoteku (dashboard).
export const markConversationRead = async (conversationId) => {
  try {
    const response = await authClient.post(
      `/conversations/${conversationId}/read`
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri oznacavanju razgovora kao procitanog.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};
